import { useDispatch } from "react-redux";

import type { GeoJSONSource } from "maplibre-gl";
import type { Feature, FeatureCollection, Geometry } from "geojson";

import { useFetchAdminAreaHierarchy } from "@/api/adminArea/userAdminAreaAPI";
import { streamDatasetChunks } from "@/api/agenticAI/streamDatasetChunks";
import { UNIQUE_ID_PROPERTY } from "@/constants/map";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useDataset } from "@/hooks/map/useDataset";
import {
  buildDatasetFilterFromPayload,
  enrichAdminAreaFilters
} from "@/hooks/project/utils";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { addFilter, addRawDataset } from "@/store/slice/datasetSlice";
import { setInputRowCount } from "@/store/slice/spatialSlice";
import type { SpatialAggregationType } from "@/types/common";
import type {
  Dataset,
  DatasetAttributeSchema,
  DatasetChunkStream,
  DatasetFilter,
  DatasetFilterPayload,
  DatasetMetadata,
  DatasetMetadataStream,
  H3Configuration
} from "@/types/dataset";
import { LayerType } from "@/types/map";
import { convertDatasetResponseToFeatureCollection } from "@/utils/dataset";
import { generateDatasetData } from "@/utils/generateDatasetData";

export type ProgressiveDatasetAddRequest = {
  datasetId: string;
  filterPayload: DatasetFilterPayload[];
  h3Configuration: H3Configuration | null;
  signal: AbortSignal;
  /** Colour-palette slot — `handleAddDataset` takes it modulo 20. */
  index: number;
  /** Fires once the stream declares its size, before anything is painted. */
  onMeta?: (meta: DatasetMetadataStream) => void;
  /**
   * Fires as soon as the layer exists, so a caller that later refuses the
   * dataset knows what to take back off the map.
   */
  onAdded?: (compositeId: string, spatialType: SpatialAggregationType) => void;
  /**
   * Fires per chunk with the rows delivered so far. Abort the request's signal
   * from here to refuse a dataset mid-download.
   */
  onRows?: (delivered: number) => void;
};

export type ProgressiveDatasetAddResult = {
  compositeId: string;
  spatialType: SpatialAggregationType;
  totalRows: number;
};

/**
 * Stamps the id `promoteId` reads, numbered on from the features already in the
 * source. The source builders only stamp what they are handed at `addSource`
 * time, and `updateData` refuses a source whose features are not all uniquely
 * identified.
 */
const withUniqueIds = (
  features: Feature<Geometry>[],
  idOffset: number
): Feature<Geometry>[] =>
  features.map((feature, position) => ({
    ...feature,
    properties: {
      ...feature.properties,
      [UNIQUE_ID_PROPERTY]:
        feature.properties?.[UNIQUE_ID_PROPERTY] || idOffset + position
    }
  }));

const needsAdminAreaEnrichment = (filters: DatasetFilter[]) =>
  filters.some((filter) => filter.attributeCode?.includes("adm_area_level"));

/**
 * Loads a dataset onto the shared map while it is still streaming.
 *
 * Rows arrive in many small frames, so they are buffered and flushed one batch
 * per animation frame, which is what makes the fill visible. Appends go through
 * `GeoJSONSource.updateData`, which merges diffs; `setData` instead replaces its
 * pending update and re-encodes the whole collection on the main thread, so a
 * burst of appends would collapse into one render of the final state.
 *
 * Colour breaks are quantiles over the whole column and so are wrong on a
 * partial buffer, so the layer is rebuilt once from the complete result when the
 * stream closes. That rebuild is also the second and last camera move.
 */
export const useProgressiveDatasetAdd = () => {
  const dispatch = useDispatch();
  const { handleAddDataset } = useDataset();
  const {
    map,
    handleAddPointsLayer,
    handleAddPolygonLayer,
    handleAddH3GeohashLayer,
    handleAddLineLayer,
    handleRemoveSourceLayer
  } = useMap();
  const fetchHierarchy = useFetchAdminAreaHierarchy();
  const { isAdminTreeEnabled } = useAppFeatures();

  const paintLayer = (
    dataset: Dataset,
    collection: FeatureCollection<Geometry>
  ) => {
    const spatialType = dataset.properties.spatialType;

    if (
      spatialType === LayerType.POLYGON ||
      spatialType === LayerType.ADMINISTRATIVE
    ) {
      handleAddPolygonLayer(dataset, collection);
    } else if (spatialType === LayerType.POINT) {
      handleAddPointsLayer(dataset, collection);
    } else if (
      spatialType === LayerType.H3 ||
      spatialType === LayerType.GEOHASH
    ) {
      handleAddH3GeohashLayer(dataset, collection);
    } else if (spatialType === LayerType.LINE) {
      handleAddLineLayer(dataset, collection);
    }
  };

  const addDatasetProgressively = async ({
    datasetId,
    filterPayload,
    h3Configuration,
    signal,
    index,
    onMeta,
    onAdded,
    onRows
  }: ProgressiveDatasetAddRequest): Promise<ProgressiveDatasetAddResult> => {
    let streamMeta: DatasetMetadataStream | undefined;
    let datasetDetail: DatasetMetadata | undefined;
    let schema: DatasetAttributeSchema[] = [];
    let hierarchyPromise: Promise<Awaited<ReturnType<typeof fetchHierarchy>>> =
      Promise.resolve([]);
    let baseFilters: DatasetFilter[] = [];
    let addedDataset: Dataset | undefined;

    /** How many features the source already holds, for id continuation. */
    let paintedFeatureCount = 0;
    /** Set when the source rejects diffs; the fill stops, the rebuild still lands. */
    let appendsDisabled = false;

    const pendingRows: DatasetChunkStream["rows"] = [];
    let scheduledFrameId: number | null = null;
    let flushQueue: Promise<void> = Promise.resolve();

    const buildCollection = (
      rows: DatasetChunkStream["rows"],
      idOffset: number
    ) => {
      const { datasetData } = generateDatasetData(
        { dataset_id: datasetId, rows, seq: 0 },
        streamMeta!,
        true,
        h3Configuration
      );

      return {
        datasetData,
        features: withUniqueIds(
          convertDatasetResponseToFeatureCollection(datasetData, schema)
            .features,
          idOffset
        )
      };
    };

    const flush = async () => {
      if (signal.aborted || !streamMeta || !datasetDetail) return;
      if (pendingRows.length === 0) return;

      // Take the whole buffer in one go and convert it once. Converting the
      // growing accumulation instead would be O(n²) over the stream.
      const rows = pendingRows.splice(0, pendingRows.length);

      if (!addedDataset) {
        const { datasetData, features } = buildCollection(rows, 0);

        baseFilters = buildDatasetFilterFromPayload(filterPayload, schema);

        // The admin-area hierarchy is deliberately not awaited here: it only
        // rewrites filter labels, and when cold it is a multi-second request
        // that would hold up every batch behind it. The enriched filters are
        // dispatched once the stream is done instead.
        addedDataset = await handleAddDataset({
          datasetMetadata: datasetDetail,
          datasetData,
          savedFilters: baseFilters,
          attributesSchema: schema,
          h3Configuration,
          index
        });

        paintedFeatureCount = features.length;
        onAdded?.(addedDataset.data.id, addedDataset.properties.spatialType);
        return;
      }

      if (appendsDisabled) return;

      const { features } = buildCollection(rows, paintedFeatureCount);
      const source = map?.getSource<GeoJSONSource>(addedDataset.data.id);

      if (!source) return;

      try {
        source.updateData({ add: features });
        paintedFeatureCount += features.length;
      } catch (error) {
        // The source refuses diffs when its features are not all uniquely
        // identified. Stop appending rather than fall back to `setData`, whose
        // per-call re-encode of everything is worse than no fill at all: the
        // rebuild below still paints the complete result.
        appendsDisabled = true;
        console.warn(
          "Progressive append unavailable for this dataset; it will appear when the stream completes.",
          error
        );
      }
    };

    const scheduleFlush = () => {
      if (scheduledFrameId !== null) return;

      scheduledFrameId = requestAnimationFrame(() => {
        scheduledFrameId = null;
        flushQueue = flushQueue.then(flush);
      });
    };

    const cancelScheduledFlush = () => {
      if (scheduledFrameId === null) return;

      cancelAnimationFrame(scheduledFrameId);
      scheduledFrameId = null;
    };

    try {
      const { rawDataset, totalRows } = await streamDatasetChunks({
        datasetId,
        filterPayload,
        h3Configuration,
        signal,
        onMeta: (meta, detail) => {
          streamMeta = meta;
          datasetDetail = detail;
          schema = detail.schema;
          hierarchyPromise = detail.country_id
            ? fetchHierarchy(detail.country_id).catch(() => [])
            : Promise.resolve([]);
          onMeta?.(meta);
        },
        onChunk: (rows, delivered) => {
          for (const row of rows) pendingRows.push(row);

          onRows?.(delivered);
          scheduleFlush();
        }
      });

      // Whatever arrived after the last frame still has to land before the
      // rebuild reads the result.
      cancelScheduledFlush();
      await flushQueue;
      await flush();

      if (!addedDataset) {
        throw new Error(
          `The dataset stream for ${datasetId} delivered no rows to paint.`
        );
      }

      const compositeId = addedDataset.data.id;
      const spatialType = addedDataset.properties.spatialType;

      // Quantile breaks, the legend and the ranking cap were all computed from
      // the first batch. Rebuilding from the complete result makes them describe
      // the real distribution, and frames the camera on all of it.
      const completeCollection = convertDatasetResponseToFeatureCollection(
        rawDataset,
        schema
      );

      handleRemoveSourceLayer(compositeId, spatialType);
      paintLayer(addedDataset, completeCollection);

      dispatch(addRawDataset({ dataset: rawDataset, id: compositeId }));

      if (isAdminTreeEnabled) {
        dispatch(
          setInputRowCount({ datasetId: compositeId, count: totalRows })
        );
      }

      if (needsAdminAreaEnrichment(baseFilters)) {
        dispatch(
          addFilter({
            datasetId: compositeId,
            filter: enrichAdminAreaFilters(baseFilters, await hierarchyPromise)
          })
        );
      }

      return { compositeId, spatialType, totalRows };
    } finally {
      cancelScheduledFlush();
    }
  };

  return { addDatasetProgressively };
};
